import type {
  ExtendedURI,
  ExtendedUserAgentOptions,
  InviteOptionsConfig,
} from './index.types';
import {
  ICE_CHECKING_TIMEOUT_MS,
  ICE_GATHERING_TIMEOUT_MS,
  TRANSPORT_OPTIONS_CONNECTION_TIMEOUT_SEC,
  TRANSPORT_OPTIONS_MAX_RECONNECTION_ATTEMPTS,
  TRANSPORT_OPTIONS_RECONNECTION_TIMEOUT_SEC,
  USER_AGENT_STOP_DELAY_MS,
} from 'Modules/softphone/constants';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { SipConfig } from 'Modules/softphone/types';
import { sipLogger as logger } from 'Modules/softphone/utils/logger';
import { normalizeSipUri } from 'Modules/softphone/utils/sip';
import { parseSipMessage } from 'Modules/softphone/utils/sipTrace';
import { SdpService } from 'Services/voipSdpService';
import {
  UserAgent,
  Registerer,
  RegistererState,
  Inviter,
  InviterInviteOptions,
  InvitationAcceptOptions,
  SessionDescriptionHandlerModifier,
} from 'sip.js';
import type { IncomingResponseMessage } from 'sip.js/lib/core';

export type {
  ExtendedURI,
  ExtendedUserAgentOptions,
  InviteOptionsConfig,
} from './index.types';

/**
 * Read a WebSocket frame as text.
 *
 * sip.js asks the socket for `arraybuffer`, so a binary frame decodes here and
 * keeps its place in the timeline instead of arriving later out of order.
 */
const readFrameAsText = (data: unknown): string => {
  if (typeof data === 'string') {
    return data;
  }

  // Duck-typed rather than `instanceof ArrayBuffer`: the buffer can come from a
  // different JS realm (a worker, or jsdom under test), where `instanceof` is
  // false for the very same kind of object.
  if (data && typeof data === 'object' && 'byteLength' in data) {
    try {
      return new TextDecoder().decode(data as ArrayBuffer);
    } catch {
      return '';
    }
  }

  return '';
};

/**
 * Map a SIP `Call-ID` onto the leg the panel knows it by.
 *
 * Traced messages carry the protocol's own identifier, which is not the
 * softphone's call id. Without this translation every message on the wire would
 * fall into the timeline's "no call" bucket and a warm transfer's two legs — or
 * a conference's five — could not be told apart.
 *
 * Read from the store at trace time rather than kept in a map here: the store is
 * already the one place that knows which sessions exist, and a second copy could
 * only go stale.
 */
const resolveLegIds = (
  sipCallId: string | undefined
): { callId?: string; conferenceId?: string } => {
  if (!sipCallId) {
    return {};
  }

  const { activeCalls, activeConference } = useSoftphoneStore.getState();

  for (const call of activeCalls.values()) {
    // `request` covers a leg that has not reached a dialog yet (its INVITE and
    // the provisional responses); `dialog` covers re-INVITEs and the BYE.
    const sessionCallId =
      call.session?.request?.callId ?? call.session?.dialog?.callId;

    if (sessionCallId === sipCallId) {
      return { callId: call.id };
    }
  }

  // Conference room traffic is tagged with the roomGuid, the same id the
  // conference handler puts on its own events.
  if (activeConference?.roomSession?.dialog?.callId === sipCallId) {
    return { conferenceId: activeConference.roomGuid };
  }

  return {};
};

/**
 * Manages SIP.js UserAgent for VoIP signaling.
 * Handles registration, outbound/inbound calls, and codec configuration (SDP modifiers).
 */
export class SipService {
  private static instance: SipService;
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private config: SipConfig | null = null;
  private isInitializing = false;
  private isInitialized = false;
  private eventService = EventService.getInstance();
  private onTransportConnectCallback: (() => void) | null = null;
  /** The socket already being traced, so a reconnect taps the new one only once. */
  private tracedSocket: WebSocket | null = null;
  /** Kept so the listener can be detached, rather than left to the socket dying. */
  private tracedSocketListener: ((event: MessageEvent) => void) | null = null;
  /** Whether the stack incoming tap is installed. See `traceStackMessages`. */
  private isTracingStackMessages = false;
  /** The frame just handed to the stack, so the socket tap does not repeat it. */
  private frameDeliveredToStack: string | null = null;

  private constructor() {}

  setOnTransportConnect(callback: (() => void) | null): void {
    this.onTransportConnectCallback = callback;
  }

  static getInstance(): SipService {
    if (!SipService.instance) {
      SipService.instance = new SipService();
    }
    return SipService.instance;
  }

  async initialize(config: SipConfig): Promise<void> {
    if (this.isInitializing) {
      logger.warn('Already initializing, skipping duplicate call');
      return;
    }

    if (this.isInitialized && this.userAgent) {
      logger.warn('Already initialized, skipping');
      return;
    }

    // This ensures no stale credentials or state from previous connection
    if (this.userAgent || this.registerer) {
      logger.warn('Existing UserAgent/Registerer found, performing full cleanup before re-initialization');

      if (this.registerer) {
        try {
          if (this.registerer.state === RegistererState.Registered) {
            logger.debug('Unregistering existing Registerer...');
            await this.registerer.unregister();
          }
          void this.registerer.dispose();
        } catch (err) {
          logger.error('Error cleaning up existing Registerer', err as Error);
        }
        this.registerer = null;
      }

      if (this.userAgent) {
        try {
          logger.debug('Stopping existing UserAgent...');
          await this.userAgent.stop();
          // Wait for cleanup to complete
          await new Promise((resolve) =>
            setTimeout(resolve, USER_AGENT_STOP_DELAY_MS)
          );
        } catch (err) {
          logger.error('Error stopping existing UserAgent', err as Error);
        }
        this.userAgent = null;
        // The next UserAgent brings its own socket to tap.
        this.stopTracingSocket();
        this.isTracingStackMessages = false;
      }

      this.isInitialized = false;
    }

    this.isInitializing = true;

    try {
      logger.info('Initializing with config', {
        uri: config.uri,
        domain: config.domain,
        wsServers: config.wsServers,
      });

      this.config = config;

      const uriString = normalizeSipUri(config.uri);

      logger.debug(`Creating URI from: ${uriString}`);

      let uri: ExtendedURI | undefined;
      try {
        uri = UserAgent.makeURI(uriString) as ExtendedURI | undefined;
        logger.debug(`UserAgent.makeURI returned: ${uri?.toString()}`);
      } catch (uriError) {
        logger.error('UserAgent.makeURI threw error', {
          error: (uriError as Error).message,
        });
        throw uriError;
      }

      if (!uri) {
        logger.error(`Failed to create URI from: ${uriString}`);
        throw new Error('Invalid SIP URI');
      }

      logger.debug(`Created SIP URI: ${uri.toString()}`);

      const userAgentOptions: ExtendedUserAgentOptions = {
        uri,
        authorizationUsername: config.authorizationUsername,
        authorizationPassword: config.authorizationPassword,
        logBuiltinEnabled: true,
        logLevel: 'debug',
        // Enable automatic reconnection at UserAgent level
        reconnectionAttempts: TRANSPORT_OPTIONS_MAX_RECONNECTION_ATTEMPTS,
        reconnectionDelay: TRANSPORT_OPTIONS_RECONNECTION_TIMEOUT_SEC,
        transportOptions: {
          wsServers: config.wsServers,
          connectionTimeout: TRANSPORT_OPTIONS_CONNECTION_TIMEOUT_SEC,
          reconnectionTimeout: TRANSPORT_OPTIONS_RECONNECTION_TIMEOUT_SEC,
          maxReconnectionAttempts: TRANSPORT_OPTIONS_MAX_RECONNECTION_ATTEMPTS,
          traceSip: true,
        },
        sessionDescriptionHandlerFactoryOptions: {
          constraints: {
            audio: true,
            video: false,
          },
          // Reduced timeouts to minimize audio connection delay
          iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
          iceCheckingTimeout: ICE_CHECKING_TIMEOUT_MS,
          rtcConfiguration: {
            rtcpMuxPolicy: 'require',
            iceTransportPolicy: 'relay',
            iceCandidatePoolSize: 1,
          },
        },
      };

      logger.debug('Creating UserAgent...');
      this.userAgent = new UserAgent(userAgentOptions);

      // Setup transport event handlers
      this.setupTransportHandlers();

      // Start the UserAgent
      logger.debug('Starting UserAgent...');
      await this.userAgent.start();
      logger.info('UserAgent started successfully');

      this.isInitialized = true;
    } catch (error) {
      logger.error('Initialization failed', {
        error: (error as Error).message,
      });
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  private setupTransportHandlers(): void {
    if (!this.userAgent) {
      logger.warn('Cannot setup transport handlers: UserAgent is null');
      return;
    }

    logger.debug('Setting up transport handlers...');

    this.setupSipTracing();

    this.userAgent.transport.onConnect = () => {
      logger.info('Transport connected');

      // A reconnect builds a new socket, so the discarded-frame tap is
      // (re)attached here.
      this.traceDiscardedFrames();

      if (this.onTransportConnectCallback) {
        try {
          this.onTransportConnectCallback();
        } catch (err) {
          logger.error('Error in transport connect callback', err as Error);
        }
      }

      // CRITICAL: Only re-register if we have a valid registerer AND it's unregistered
      // Do NOT re-register if we're in the middle of cleanup or reinitialization
      if (
        this.registerer &&
        this.registerer.state === RegistererState.Unregistered &&
        this.isInitialized // Only re-register if we're fully initialized
      ) {
        logger.info('Transport reconnected, re-registering...');
        void this.registerer.register().catch((error) => {
          logger.error('Failed to re-register after reconnection', {
            error: error instanceof Error ? error.message : String(error),
          });
        });
      } else {
        logger.debug('Skipping auto re-register', {
          hasRegisterer: !!this.registerer,
          registererState: this.registerer?.state,
          isInitialized: this.isInitialized,
        });
      }
    };

    this.userAgent.transport.onDisconnect = (error?: Error) => {
      const errorCode =
        error && typeof error === 'object' && 'code' in error
          ? (error as Error & { code?: number }).code
          : undefined;

      logger.warn('Transport disconnected', {
        error: error instanceof Error ? error.message : String(error),
        code: errorCode,
      });

      // The UserAgent will automatically attempt to reconnect
      // based on reconnectionAttempts and reconnectionDelay settings
    };

    logger.debug('Transport handlers setup complete');
  }

  /**
   * Record every SIP message that crosses the WebSocket.
   *
   * The panel used to show a hand-written event per feature — one wherever some
   * code had decided to emit one — so the SIP tab was a summary of what the app
   * believed it had done, not of what it and the server exchanged. Anything
   * nobody had written a line for (provisional responses, in-dialog re-INVITEs,
   * NOTIFYs, retransmissions, error responses) was missing altogether, which is
   * exactly the traffic a misbehaving call needs.
   *
   * Outgoing is complete here: `Transport.send` is the single path to
   * `WebSocket.send`, and the stack's own internal calls go through it too.
   * Incoming is taken from two taps that complete each other — the stack
   * callback for order, the socket for the frames the stack never sees. See
   * `traceStackMessages` and `traceDiscardedFrames`.
   */
  private setupSipTracing(): void {
    const transport = this.userAgent?.transport;

    if (!transport) {
      logger.warn('Cannot trace SIP messages: transport is unavailable');
      return;
    }

    this.traceStackMessages(transport);

    if (typeof transport.send === 'function') {
      // Bound up front: calling `transport.send` from inside the replacement
      // would call the replacement. The cast is only needed because `bind` on a
      // method of an interface-typed value widens to `any` here.
      const sendToWire = transport.send.bind(transport) as (
        message: string
      ) => Promise<void>;

      transport.send = (message: string) => {
        this.traceWireMessage('outgoing', message);
        return sendToWire(message);
      };
    }
  }

  /**
   * Primary incoming tap: the callback the stack is handed.
   *
   * Logged BEFORE the message is delivered, which is what keeps the timeline in
   * wire order. The stack answers a message synchronously — a 401 challenge is
   * read and the next REGISTER written inside this same call — so anything that
   * records the message after delivery records it after the reply it caused.
   */
  private traceStackMessages(transport: UserAgent['transport']): void {
    if (this.isTracingStackMessages) {
      return;
    }

    this.isTracingStackMessages = true;
    const deliverToStack = transport.onMessage;

    transport.onMessage = (message: string) => {
      // Marked before logging, so the socket tap below can tell this frame was
      // delivered and must not be recorded twice.
      this.frameDeliveredToStack = message;
      this.traceWireMessage('incoming', message);
      deliverToStack?.(message);
    };
  }

  /**
   * Second incoming tap: the frames sip.js throws away.
   *
   * `onMessage` alone is not everything that arrives. sip.js discards frames
   * before ever calling it — anything received while the transport is not in
   * the "Connected" state, plus empty and keep-alive frames — and a message the
   * library dropped is exactly what makes a call log look complete when it is
   * not. The socket listener sees every frame.
   *
   * It cannot be the only tap, though: it is registered after sip.js's own
   * listener, so it runs after the stack has already answered. Hence the pair —
   * the stack tap gives the order, this one gives the frames nobody else saw,
   * and `frameDeliveredToStack` keeps a delivered frame from being logged by
   * both.
   *
   * Runs on each transition to "Connected" because a reconnect builds a new
   * socket; the identity guard keeps one listener per socket.
   */
  private traceDiscardedFrames(): void {
    const transport = this.userAgent?.transport;

    // `ws` belongs to the WebSocket transport implementation, not to the
    // Transport interface the UserAgent is typed with.
    const socket = (transport as { ws?: WebSocket } | undefined)?.ws;

    if (!socket || socket === this.tracedSocket) {
      return;
    }

    // A reconnect replaces the socket, and the old one can still be draining.
    // Detaching is not left to the socket being collected: while both are
    // alive, both listeners would report.
    this.stopTracingSocket();

    const listener = (event: MessageEvent) => {
      const frame = readFrameAsText(event.data);

      // sip.js's listener was registered first, so by now it has run: either it
      // handed this frame to the stack (already logged, in the right place) or
      // it dropped it and nothing recorded it.
      if (frame === this.frameDeliveredToStack) {
        this.frameDeliveredToStack = null;
        return;
      }

      this.traceWireMessage('incoming', frame, { deliveredToStack: false });
    };

    this.tracedSocket = socket;
    this.tracedSocketListener = listener;
    socket.addEventListener('message', listener);
  }

  /**
   * Detach the socket listener, if one is attached.
   * Safe to call when there is none, so every teardown path can call it.
   */
  private stopTracingSocket(): void {
    if (this.tracedSocket && this.tracedSocketListener) {
      this.tracedSocket.removeEventListener(
        'message',
        this.tracedSocketListener
      );
    }

    this.tracedSocket = null;
    this.tracedSocketListener = null;
    this.frameDeliveredToStack = null;
  }

  /**
   * Turn one raw message into a timeline row.
   *
   * Never allowed to throw: tracing is diagnostics, and a parser mistake must
   * not stop a message from reaching the stack or the wire.
   */
  private traceWireMessage(
    direction: 'incoming' | 'outgoing',
    raw: string,
    extraDetails?: Record<string, unknown>
  ): void {
    try {
      const parsed = parseSipMessage(raw, direction);

      // The CRLF keep-alive carries no SIP; a row per ping would bury the call.
      if (parsed.isKeepAlive) {
        return;
      }

      this.eventService.logSipMessage(direction, parsed.summary, {
        ...resolveLegIds(parsed.sipCallId),
        sipCallId: parsed.sipCallId,
        cseq: parsed.cseq,
        statusCode: parsed.statusCode,
        raw: parsed.raw,
        ...extraDetails,
      });
    } catch (error) {
      logger.warn('Failed to trace SIP message', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Register with the SIP server.
   *
   * Emits no events of its own: the REGISTER, the 401 challenge, the second
   * REGISTER carrying credentials and the 200 OK are all traced off the socket.
   * The hand-written pair that used to stand in for them showed two rows for
   * four messages, invented a 401 for every transition to Unregistered whatever
   * its cause, and duplicated the two real rows once tracing existed.
   * Registration state itself is reported by the SIP tab, not by the timeline.
   */
  async register(): Promise<void> {
    if (!this.userAgent) {
      logger.error('Cannot register: UserAgent not initialized');
      throw new Error('UserAgent not initialized');
    }

    if (this.registerer) {
      logger.warn('Registerer already exists, disposing and creating new one');
      try {
        if (this.registerer.state === RegistererState.Registered) {
          await this.registerer.unregister();
        }
        // Dispose is synchronous - clean up all listeners and state
        void this.registerer.dispose();
      } catch (err) {
        logger.error('Error disposing existing Registerer', err as Error);
      }
      this.registerer = null;
    }

    // Log the credentials being used for registration (mask password)
    const config = this.config;
    if (config) {
      logger.info('Creating Registerer with credentials', {
        uri: config.uri,
        authorizationUsername: config.authorizationUsername,
        passwordLength: config.authorizationPassword?.length || 0,
        hasPassword: !!config.authorizationPassword,
      });
    }

    logger.debug('Creating new Registerer...');
    this.registerer = new Registerer(this.userAgent);

    return new Promise((resolve, reject) => {
      let isResolved = false;
      let attemptCount = 0;

      this.registerer?.stateChange.addListener((state: RegistererState) => {
        attemptCount++;
        logger.debug(`Registerer state changed to: ${state} (attempt ${attemptCount})`);

        if (isResolved) {
          logger.warn(`State change after resolution: ${state} (ignored)`);
          return; // Prevent multiple resolve/reject calls
        }

        switch (state) {
          case RegistererState.Registered:
            logger.info('Registration successful!');
            isResolved = true;
            resolve();
            break;

          case RegistererState.Unregistered:
            logger.error(`Registration failed - transitioned to Unregistered state after ${attemptCount} attempts`);
            isResolved = true;
            reject(new Error('Registration failed: Authentication error (401 Unauthorized)'));
            break;

          case RegistererState.Terminated:
            logger.warn('Registration terminated');
            isResolved = true;
            reject(new Error('Registration terminated'));
            break;

          default:
            logger.debug(`Transitional state: ${state}`);
            break;
        }
      });

      logger.debug('Sending REGISTER request...');

      this.registerer?.register().catch((error) => {
        logger.error('REGISTER request failed', {
          error: (error as Error).message,
        });
        if (!isResolved) {
          isResolved = true;
          reject(error);
        }
      });
    });
  }

  async unregister(): Promise<void> {
    if (this.registerer) {
      try {
        if (this.registerer.state === RegistererState.Registered) {
          logger.debug('Unregistering from SIP server...');
          await this.registerer.unregister();
        }
        logger.debug('Disposing Registerer...');
        void this.registerer.dispose();
      } catch (err) {
        logger.error('Error during unregister', err as Error);
      } finally {
        this.registerer = null;
      }
    }
  }

  /**
   * Create an outbound call
   * Returns the Inviter session that needs to be configured with requestDelegate
   *
   * Based on legacy PhoneStore implementation:
   * - For internal calls (personId): adds 'pr' prefix (e.g., 'pr123')
   * - For external calls (phoneNumber): removes special characters: () - and spaces
   * - Keeps the number as-is for SIP routing
   */
  makeCall(target: string, personId?: string): Inviter | null {
    if (!this.userAgent || !this.config) {
      logger.error('Cannot make call: UserAgent not initialized');
      return null;
    }

    let formattedTarget: string;

    if (personId) {
      formattedTarget = 'pr' + personId;
      logger.debug(
        `Internal call - personId: ${personId} -> formatted: ${formattedTarget}`
      );
    } else {
      formattedTarget = target.replace(/[() -]/g, '');
      if (formattedTarget !== target) {
        logger.debug(`Cleaned phone number: ${target} -> ${formattedTarget}`);
      }
    }

    const targetUri = UserAgent.makeURI(
      `sip:${formattedTarget}@${this.config.domain}`
    );
    if (!targetUri) {
      logger.error(`Invalid target URI: ${formattedTarget}`);
      return null;
    }

    logger.info(
      `Creating inviter for: sip:${formattedTarget}@${this.config.domain} (${
        personId ? 'internal' : 'external'
      } call)`
    );

    const inviter = new Inviter(this.userAgent, targetUri, {
      earlyMedia: true,
    });

    logger.debug(`Created inviter for: ${formattedTarget}`);
    return inviter;
  }

  /**
   * Get session description handler modifiers for codec enforcement
   */
  private getSessionDescriptionHandlerModifiers(
    callUri?: string,
    phoneNumber?: string,
    sipFromUri?: string,
    sipToUri?: string,
    isFeatureFlagEnabled?: boolean
  ): SessionDescriptionHandlerModifier[] {
    const sdpService = SdpService.getInstance();

    logger.debug('Checking codec enforcement', {
      callUri,
      phoneNumber,
      sipFromUri,
      sipToUri,
      isFeatureFlagEnabled,
    });

    const isCallExternal = sdpService.analyzeIfCallIsExternal(
      callUri,
      phoneNumber,
      sipFromUri,
      sipToUri,
      true
    );

    logger.debug(`Is call external? ${isCallExternal ? 'Yes' : 'No'}`);
    logger.debug(
      `Feature flag enabled? ${isFeatureFlagEnabled ? 'Yes' : 'No'}`
    );

    const modifiers: SessionDescriptionHandlerModifier[] = [];

    if (isCallExternal && isFeatureFlagEnabled) {
      logger.info('Applying PCMU enforcement modifier for external call');

      const pcmuEnforcementModifier = (
        description: RTCSessionDescriptionInit
      ) => {
        logger.debug(`PCMU modifier called for: ${description.type}`);
        return sdpService.pcmuEnforcementModifier(description, { debug: true });
      };

      modifiers.push(pcmuEnforcementModifier);
    } else {
      logger.debug(
        `NOT applying PCMU enforcement: ${
          !isCallExternal ? 'Internal call' : 'Feature flag disabled'
        }`
      );
    }

    return modifiers;
  }

  /**
   * Configure invite options with requestDelegate and codec enforcement
   */
  createInviteOptions(
    onAccept: (response: { message: IncomingResponseMessage }) => void,
    onReject: (reason: { message: IncomingResponseMessage }) => void,
    onProgress: (response: { message: IncomingResponseMessage }) => void,
    options?: InviteOptionsConfig & { microphoneId?: string }
  ): InviterInviteOptions {
    logger.info('Creating invite options', {
      hasMicrophoneId: !!options?.microphoneId,
      microphoneId: options?.microphoneId,
      enforcePcmu: options?.enforcePcmu,
    });

    return {
      requestDelegate: {
        onAccept: (response) => {
          logger.debug('Call accepted', {
            answeredBy: response.message.getHeader('Answered-By'),
          });
          onAccept(response);
        },
        onReject: (reason) => {
          logger.debug(`Call rejected: ${reason.message.statusCode}`);
          if (reason.message.statusCode !== 487) {
            logger.error('Call rejection', {
              statusCode: reason.message.statusCode,
            });
          }
          onReject(reason);
        },
        onProgress: (response) => {
          logger.debug(`Call progress: ${response.message.statusCode}`);
          onProgress(response);
        },
      },
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: options?.microphoneId
            ? { deviceId: { ideal: options.microphoneId } }
            : true,
          video: false,
        },
      },
      sessionDescriptionHandlerModifiers:
        this.getSessionDescriptionHandlerModifiers(
          options?.callUri,
          options?.phoneNumber,
          options?.sipFromUri,
          options?.sipToUri,
          options?.enforcePcmu
        ),
    };
  }

  /**
   * Configure accept options for incoming calls with codec enforcement
   */
  createAcceptOptions(
    microphoneId?: string,
    options?: InviteOptionsConfig
  ): InvitationAcceptOptions {
    logger.info('Creating accept options', {
      hasMicrophoneId: !!microphoneId,
      microphoneId,
    });

    return {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: microphoneId ? { deviceId: { ideal: microphoneId } } : true,
          video: false,
        },
      },
      sessionDescriptionHandlerModifiers:
        this.getSessionDescriptionHandlerModifiers(
          options?.callUri,
          options?.phoneNumber,
          options?.sipFromUri,
          options?.sipToUri,
          options?.enforcePcmu
        ),
    };
  }

  async cleanup(): Promise<void> {
    logger.debug('Cleaning up');
    await this.unregister();
    if (this.userAgent) {
      await this.userAgent.stop();
      this.userAgent = null;
      // The next UserAgent brings its own socket to tap.
      this.stopTracingSocket();
      this.isTracingStackMessages = false;
    }
    this.config = null;
    this.isInitialized = false;
  }

  getUserAgent(): UserAgent | null {
    return this.userAgent;
  }

  getRegisterer(): Registerer | null {
    return this.registerer;
  }

  getConfig(): SipConfig | null {
    return this.config;
  }
}
